import { Auth } from 'auth-provider';
import { Button } from 'components/button/Button';
import { DocumentTitle } from 'components/DocumentTitle';
import { sdClient } from 'contexts/SDKClientContext';
import { EmailScreen } from 'pages/Login/EmailScreen';
import { LoginScreen } from 'pages/Login/LoginScreen';
import { PasswordScreen } from 'pages/Login/PasswordScreen';
import { ResetPasswordScreen } from 'pages/Login/ResetPasswordScreen';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { BrowserRouter } from 'react-router-dom';
import { LoginRoutes } from 'routing/LoginRoutes';
import styled from 'styled-components';
import { link } from 'styles/theme';
import { clearPostLoginUrl, setPostLoginUrl } from 'utils/login';

type SignInError = {
    code: string;
    message: string;
    name: string;
};

const cognitoLoginErrors = {
    invalidUserOrPassword: 'Incorrect username or password.',
    passwordAttemptsExceeded: 'Password attempts exceeded',
};

const loginErrors = {
    invalidUserOrPassword: 'The email address and/or password provided is incorrect.',
    passwordAttemptsExceeded:
        'Log in attempts exceeded. Please wait 30 seconds and try again.',
};

export async function logout(shouldCallLogout?: boolean) {
    if (shouldCallLogout) {
        await sdClient.logOutUser({});
    }
    Auth.signOut({ global: false });
    sessionStorage.clear();

    const allKeys = Object.keys(localStorage);
    const deletableKeys = allKeys.filter(
        (key) => !(key.includes('workbench') || key.includes('global-search'))
    );
    deletableKeys.forEach((key) => localStorage.removeItem(key));

    window.location.reload();
}

export const Heading = styled.h1`
    font-size: 2rem;
    margin: 0;
`;

export const BackButton = styled(Button)`
    font-size: 1.4rem;
    padding: 0;
    font-weight: bold;
`;

export const ResetPasswordButton = styled(Button)`
    font-size: 1.4rem;
    padding: 0;
    color: ${link};
`;

export const LoginFooter = styled.footer`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

export type LoginForm = {
    email: string;
    password?: string;
};

enum Screen {
    Email = 'email',
    Password = 'password',
    SendResetPassword = 'sendResetPassword',
}

const isErrorObj = (err: unknown): err is { code?: string; message?: string } =>
    typeof err === 'object';

/**
 * This is the real implementation of the login, implemented as a function
 * component instead of a class component.
 */
export function MainLogin() {
    const [email, setEmail] = useState('');
    const [errorMessage, setErrorMessage] = useState<string>();
    const [screen, setScreen] = useState<Screen>(Screen.Email);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
        reset,
    } = useForm<LoginForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const onSubmitEmail = handleSubmit(async (data: LoginForm) => {
        const res = await sdClient.getAuthProvider({ emailAddress: data.email });
        const authProvider = res.AuthenticationProvider;
        // Use the email from the auth provider result
        // as this will return the proper case-sensitive version
        if (authProvider) {
            // Cognito is the generic identity provider, any other response
            // must be a federated provider which does not use password
            // authentication.
            if (authProvider !== 'Cognito') {
                // Save the current URL for redirect after returning from federated sign in
                setPostLoginUrl();
                Auth.federatedSignIn({ customProvider: authProvider });
            } else {
                setEmail(res.Email);
                setScreen(Screen.Password);
            }
        }
    });

    const onSubmitPassword = handleSubmit(async (data: LoginForm) => {
        if (data.password) {
            try {
                // Clear any post login URL as it is only needed for a federated sign in
                clearPostLoginUrl();
                await Auth.signIn(data.email.toLowerCase(), data.password);

                // Authentication occurs in the background without refreshing,
                // we could use some callback here to trigger the app to rerender
                // but the easiest method is to just refresh the page
                window.location.reload();
            } catch (err) {
                if (isErrorObj(err) && err.code) {
                    if (err.message && err.code === 'NotAuthorizedException') {
                        const msg = (err as SignInError).message;
                        if (msg === cognitoLoginErrors.invalidUserOrPassword) {
                            setErrorMessage(loginErrors.invalidUserOrPassword);
                        } else if (msg === cognitoLoginErrors.passwordAttemptsExceeded) {
                            setErrorMessage(loginErrors.passwordAttemptsExceeded);
                        } else {
                            setErrorMessage(msg);
                        }
                    } else if (err.message) {
                        const msg = (err as SignInError).message;
                        setErrorMessage(msg);
                    } else {
                        setErrorMessage('Login failed.');
                    }
                }
            }
        }
    });

    const isActionable = (!isSubmitted || isValid) && !(isValidating || isSubmitting);

    return (
        <>
            {(screen === Screen.Email || screen === Screen.Password) && (
                <div className="mb-40">
                    <Heading>Welcome to SMARTdiagnostics</Heading>
                </div>
            )}
            {screen === Screen.Email && (
                <EmailScreen
                    register={register}
                    emailErrors={errors.email}
                    onSubmit={(event) => {
                        event.preventDefault();
                        onSubmitEmail();
                    }}
                    onLogin={onSubmitEmail}
                    actionable={isActionable}
                />
            )}
            {screen === Screen.Password && (
                <PasswordScreen
                    register={register}
                    control={control}
                    email={email}
                    errorMessage={errorMessage}
                    passwordErrors={errors.password}
                    onSubmit={(event) => {
                        event.preventDefault();
                        onSubmitPassword();
                    }}
                    onLogin={onSubmitPassword}
                    onBack={() => {
                        reset({
                            email,
                            password: '',
                        });
                        setErrorMessage('');
                        setScreen(Screen.Email);
                    }}
                    onResetPassword={() => {
                        setErrorMessage('');
                        setScreen(Screen.SendResetPassword);
                    }}
                    onChange={() => setErrorMessage('')}
                    actionable={isActionable}
                />
            )}
            {screen === Screen.SendResetPassword && (
                <ResetPasswordScreen
                    register={register}
                    email={email}
                    onBack={() => {
                        setScreen(Screen.Email);
                    }}
                />
            )}
        </>
    );
}

export function Login() {
    return (
        <>
            <DocumentTitle title="SMARTdiagnostics" />
            <LoginScreen>
                <BrowserRouter>
                    <LoginRoutes />
                </BrowserRouter>
            </LoginScreen>
        </>
    );
}
